'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import {
  FileWarning, AlertCircle, ExternalLink, Search, ChevronLeft, ChevronRight,
} from 'lucide-react';
import { getRecentOfferings, type OfferingFiling } from '@/lib/api-client';
import { cn, decodeHtmlEntities } from '@/lib/utils';
import { useTimezone } from '@/hooks/useTimezone';
import { formDescription, formBadgeClass, type FormFamily } from '@/lib/offeringForms';

// ── Loading skeleton ──────────────────────────────────────────────────────────

function LoadingSkeleton() {
  return (
    <div className="space-y-0">
      {Array.from({ length: 12 }).map((_, i) => (
        <div key={i} className="flex items-center gap-4 px-6 py-4 border-b border-default/50">
          <div className="w-20 h-4 bg-border-default rounded animate-pulse" />
          <div className="w-14 h-4 bg-border-default rounded animate-pulse" />
          <div className="flex-1 h-4 bg-border-default rounded animate-pulse" />
          <div className="w-16 h-5 bg-border-default rounded-full animate-pulse" />
          <div className="w-24 h-4 bg-border-default rounded animate-pulse" />
          <div className="w-16 h-4 bg-border-default rounded animate-pulse" />
        </div>
      ))}
    </div>
  );
}

// ── Helpers ────────────────────────────────────────────────────────────────────

function formatDisplayDate(dateStr: string, tz?: string): string {
  if (!dateStr) return '--';
  const d = new Date(dateStr + 'T00:00:00');
  if (isNaN(d.getTime())) return '--';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: tz });
}

type FilterValue = 'all' | FormFamily;

const PAGE_SIZE = 50;

const FILTER_BUTTONS: { label: string; value: FilterValue }[] = [
  { label: 'All', value: 'all' },
  { label: 'Prospectus (424B)', value: '424B' },
  { label: 'Shelf (S-3)', value: 'S-3' },
  { label: 'Registration (S-1)', value: 'S-1' },
  { label: 'FWP', value: 'FWP' },
];

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function RecentOfferingsPage() {
  const tz = useTimezone();
  const [items, setItems] = useState<OfferingFiling[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState<FilterValue>('all');
  const [qInput, setQInput] = useState('');
  const [q, setQ] = useState('');
  const [page, setPage] = useState(0); // 0-based

  // Debounce the search box → q, returning to the first page on a new search.
  // Resetting page here (rather than in a separate [filter,q] effect) keeps the
  // page reset in the SAME render batch as the input change, so the fetch never
  // fires once with a stale offset for the new filter/search.
  useEffect(() => {
    const id = setTimeout(() => { setQ(qInput.trim()); setPage(0); }, 300);
    return () => clearTimeout(id);
  }, [qInput]);

  // Switch the form-family filter: reset to page 1 and show the skeleton
  // immediately so the previous filter's rows never flash. Guard the no-op
  // click on the already-active chip — otherwise loading would be stranded
  // true (the fetch effect's deps wouldn't change, so it'd never re-run).
  const handleFilter = (v: FilterValue) => {
    if (v === filter) return;
    setFilter(v);
    setPage(0);
    setLoading(true);
  };

  // Fetch the current page (server-side paging + filter + search).
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    getRecentOfferings({
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE,
      form: filter === 'all' ? undefined : filter.toLowerCase(),
      q,
    })
      .then((res) => {
        if (cancelled) return;
        setItems(res.items);
        setTotal(res.total);
      })
      .catch(() => { if (!cancelled) { setError('Failed to load recent offering filings.'); setItems([]); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [page, filter, q]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const fromRow = total === 0 ? 0 : page * PAGE_SIZE + 1;
  const toRow = Math.min(total, (page + 1) * PAGE_SIZE);

  return (
    <div className="min-h-screen bg-surface text-white">
      <div className="px-2 sm:px-3 lg:px-4 py-8">

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6">
          <div>
            <div className="flex items-center gap-3 mb-1">
              <div className="w-9 h-9 rounded-lg bg-red-500/10 border border-red-500/20 flex items-center justify-center">
                <FileWarning size={18} className="text-red-400" />
              </div>
              <h1 className="text-2xl font-bold text-white">Recent Offerings</h1>
            </div>
            <p className="text-fg-muted text-sm ml-12">
              SEC dilutive offering filings (S-1 / S-3 / 424B / FWP) — potential share dilution
            </p>
          </div>

          {/* Filter buttons */}
          <div className="flex flex-wrap rounded-lg overflow-hidden border border-default self-start">
            {FILTER_BUTTONS.map((fb) => (
              <button
                key={fb.value}
                onClick={() => handleFilter(fb.value)}
                className={cn(
                  'px-3.5 py-1.5 text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-sky-400',
                  filter === fb.value
                    ? 'bg-red-500 text-white'
                    : 'bg-card-bg text-fg-muted hover:text-slate-200',
                )}
              >
                {fb.label}
              </button>
            ))}
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="flex items-center gap-3 p-4 bg-red-500/10 border border-red-500/20 rounded-xl mb-6 text-red-400">
            <AlertCircle size={18} />
            <span className="text-sm">{error}</span>
          </div>
        )}

        {/* Table */}
        <div className="bg-card-bg border border-default rounded-xl overflow-hidden">
          <div className="flex items-center justify-between gap-3 px-6 py-4 border-b border-default">
            <div className="flex items-center gap-2">
              <FileWarning size={16} className="text-red-400" />
              <span className="text-sm font-medium text-slate-200">Dilutive Filings</span>
              {!loading && !error && (
                <span className="text-xs text-fg-subtle">
                  · {total.toLocaleString()} filing{total !== 1 ? 's' : ''}
                </span>
              )}
            </div>

            {/* Symbol / company search */}
            <div className="relative">
              <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-fg-subtle pointer-events-none" />
              <input
                type="text"
                value={qInput}
                onChange={(e) => setQInput(e.target.value)}
                placeholder="Symbol or company…"
                className="w-32 sm:w-52 pl-8 pr-2 py-1.5 text-xs bg-surface/60 border border-default rounded-lg text-slate-200 placeholder:text-slate-600 focus:outline-none focus:border-red-500 focus:ring-1 focus:ring-red-500/60"
              />
            </div>
          </div>

          {loading && <LoadingSkeleton />}

          {!loading && !error && items.length === 0 && (
            <div className="flex flex-col items-center justify-center py-20 gap-4 text-center">
              <div className="w-14 h-14 rounded-full bg-card-bg border border-default flex items-center justify-center">
                <FileWarning size={22} className="text-slate-600" />
              </div>
              <div>
                <p className="text-fg-muted font-medium">No offering filings found</p>
                <p className="text-slate-600 text-sm mt-1">
                  {q || filter !== 'all' ? 'Try changing the filter or search.' : 'Check back later.'}
                </p>
              </div>
            </div>
          )}

          {!loading && !error && items.length > 0 && (
            <>
              {/* Desktop table */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-default bg-surface/40">
                      <th className="text-left px-6 py-3 text-xs font-semibold text-fg-subtle uppercase tracking-wide">Filed</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-fg-subtle uppercase tracking-wide">Symbol</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-fg-subtle uppercase tracking-wide">Company</th>
                      <th className="text-center px-4 py-3 text-xs font-semibold text-fg-subtle uppercase tracking-wide">Form</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-fg-subtle uppercase tracking-wide">Type</th>
                      <th className="text-right px-6 py-3 text-xs font-semibold text-fg-subtle uppercase tracking-wide">Filing</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((o, idx) => (
                      <tr
                        key={o.id}
                        className={cn(
                          'border-b border-default/50 transition-colors hover:bg-card-hover-bg',
                          idx % 2 === 0 ? 'bg-transparent' : 'bg-surface/20',
                        )}
                      >
                        <td className="px-6 py-4 text-slate-300 font-mono text-sm whitespace-nowrap">
                          {formatDisplayDate(o.filedDate, tz)}
                        </td>
                        <td className="px-4 py-4">
                          <Link
                            href={`/stocks/${o.symbol}`}
                            className="font-bold text-sky-400 hover:text-sky-300 transition-colors"
                          >
                            {o.symbol}
                          </Link>
                        </td>
                        <td className="px-4 py-4 text-slate-300 text-xs max-w-[260px] truncate">
                          {o.issuerName ? decodeHtmlEntities(o.issuerName) : '—'}
                        </td>
                        <td className="px-4 py-4 text-center">
                          <span
                            className={cn('inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold font-mono', formBadgeClass(o.form))}
                            title={formDescription(o.form)}
                          >
                            {o.form}
                          </span>
                        </td>
                        <td className="px-4 py-4 text-fg-muted text-xs">{formDescription(o.form)}</td>
                        <td className="px-6 py-4 text-right">
                          {o.secUrl ? (
                            <a
                              href={o.secUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 text-xs font-medium text-sky-400 hover:text-sky-300 transition-colors"
                            >
                              EDGAR <ExternalLink size={11} />
                            </a>
                          ) : (
                            <span className="text-slate-600 text-xs">—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile cards */}
              <div className="md:hidden divide-y divide-border-default/50">
                {items.map((o) => (
                  <div key={o.id} className="px-4 py-4 hover:bg-card-hover-bg transition-colors">
                    <div className="flex items-center justify-between mb-1.5">
                      <div className="flex items-center gap-2">
                        <Link
                          href={`/stocks/${o.symbol}`}
                          className="font-bold text-sky-400 hover:text-sky-300 transition-colors text-sm"
                        >
                          {o.symbol}
                        </Link>
                        <span
                          className={cn('inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold font-mono', formBadgeClass(o.form))}
                          title={formDescription(o.form)}
                        >
                          {o.form}
                        </span>
                      </div>
                      {o.secUrl && (
                        <a
                          href={o.secUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-xs font-medium text-sky-400 hover:text-sky-300"
                        >
                          EDGAR <ExternalLink size={11} />
                        </a>
                      )}
                    </div>
                    {o.issuerName && (
                      <p className="text-xs text-slate-300 truncate">{decodeHtmlEntities(o.issuerName)}</p>
                    )}
                    <div className="flex items-center gap-3 text-xs text-fg-subtle mt-1">
                      <span>{formatDisplayDate(o.filedDate, tz)}</span>
                      <span>· {formDescription(o.form)}</span>
                    </div>
                  </div>
                ))}
              </div>

              {/* Pagination */}
              {total > 0 && (
                <div className="flex items-center justify-between gap-3 px-4 sm:px-6 py-3 border-t border-default">
                  <span className="text-xs text-fg-subtle">
                    {fromRow.toLocaleString()}–{toRow.toLocaleString()} of {total.toLocaleString()}
                  </span>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setPage((p) => Math.max(0, p - 1))}
                      disabled={page <= 0}
                      className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-lg border border-default bg-card-bg text-slate-200 transition-colors hover:bg-card-hover-bg disabled:opacity-40 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-sky-400"
                    >
                      <ChevronLeft size={14} /> Prev
                    </button>
                    <span className="text-xs text-fg-muted tabular-nums">
                      Page {page + 1} / {totalPages}
                    </span>
                    <button
                      onClick={() => setPage((p) => (p + 1 < totalPages ? p + 1 : p))}
                      disabled={page + 1 >= totalPages}
                      className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-lg border border-default bg-card-bg text-slate-200 transition-colors hover:bg-card-hover-bg disabled:opacity-40 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-sky-400"
                    >
                      Next <ChevronRight size={14} />
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <div className="mt-6 text-xs text-slate-600">
          Data sourced from SEC EDGAR full-text search. Filings may be delayed. A registration or prospectus
          filing signals a company <em>may</em> issue new shares — it is a dilution risk, not a completed sale.
        </div>
      </div>
    </div>
  );
}
